import { useEffect, useState } from "react";
import $ from "jquery";
import { assetUrl } from "fusion-core";
import { Helmet } from "fusion-plugin-react-helmet-async";
import BuyStep from "../enums/BuyStep";
import { useNavigate } from "fusion-plugin-react-router";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import useBuyStore from "../store/BuyStore";
import { useForm } from "react-hook-form";
import Loader from "../../../shared/components/Loader";
import { fetchHpp } from "../../quote/services/quoteService";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { scrollToTop } from "../../../shared/utils/scrollUtils";
import { handlePaymentSuccess } from "../services/buyPaymentFlowService";
import ModalWrapper from "../../../shared/components/ModalWrapper";

const TbGlobalPaymentsPage = () => {
  const navigate = useNavigate();
  let STATE = useAppStateStore();
  const [HppURL, setHppURL] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isGlobalPaymentsErrorModal, setIsGlobalPaymentsErrorModal] = useState(
    false
  );
  const [paymentHandled, setPaymentHandled] = useState(false);

  const buyStore = useBuyStore.getState();
  const selectedPaymentOption = buyStore.getObject("selectedPaymentOption");

  // Form Setup
  const { watch, setValue } = useForm();

  const cardCss = assetUrl("../../../styles/tqPanelCard.css");
  const paymentsCss = assetUrl("../../../styles/payments.css");
  const paymentjson = buyStore.getObject("jsonPaymentData");

  const resultCodeMap = {
    "101":
      "Decline - Generally insufficient funds or incorrect card data supplied, for example, expiry date or security code.",
    "102":
      "Referral B - Contact the Authorization Center. Usually treated as a standard decline for Ecommerce systems, the transaction can still be processed using a Manual request once an authorization code is obtained.",
    "103":
      "Referral A - The card has been reported lost or stolen. Usually treated as a standard decline for ecommerce systems.",
    "111":
      "Soft Decline - Indicates that the Issuer requires Strong Customer Authentication (SCA) for a transaction. In order to avoid this outcome, please ensure you enable 3D Secure 2.",
    "120":
      "Return when the settlement amount is 200% or over of the original authorized amount. Only applicable to test accounts domiciled in North America (CA/US).",
    "121":
      "Return when the settlement amount is 25% or less of the original authorized amount. Only applicable to test accounts domiciled in North America (CA/US).",
    "13x":
      "Response from the Payment Declined Response Code (PDRC) service. See our PDRC article.",
    "2xx":
      "Error with bank systems – generally you can tell the customer to try again later. The resolution time depends on the issue.",
    "205": "Comms Error - installment connection error.",
    "3xx":
      "Error with Global Payments systems – generally you can tell the customer to try again later. The resolution time depends on the issue.",
    "5xx":
      "Invalid content in request message. These can be due to a formatting issue or account configuration. Please see the various 5xx examples below.",
    "666":
      "Account deactivated – your Global Payments account has been suspended. Please contact Global Payments.",
    "00": "Success",
  };

  function getDeclineMessage(resultCode) {
    return (
      resultCodeMap[resultCode] ||
      "Unknown error – please try again or contact support."
    );
  }

  const setModal = (response) => {
    setErrorMessage(getDeclineMessage(response.RESULT));
    setIsGlobalPaymentsErrorModal(true);
  };

  const localMethod = (response, navigate) => {
    handlePaymentSuccess(
      response,
      navigate,
      STATE.setPaymentProcessLoading,
      setValue,
      watch
    );
  };

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TBGLOBALPAYMENTSPAGE_ENTERED);
  }, []);

  useEffect(() => {
    scrollToTop();
    async function getHppUrl() {
      const { hppUrl } = await fetchHpp();
      setHppURL(hppUrl);
    }
    // unsetModal();
    getHppUrl();
  }, []);

  useEffect(() => {
    if (!paymentjson || Object.keys(paymentjson).length === 0 || !HppURL) {
      // Don't try to init until both are ready
      return;
    }
    // Wait for RealexHpp to be available on window
    let interval;
    function tryInit() {
      if (window.RealexHpp) {
        window.RealexHpp.setHppUrl(HppURL);
        $(document).ready(function() {
          window.RealexHpp.embedded.init(
            "autoload",
            "iframeId",
            (response, close) => {
              if (paymentHandled) {
                return;
              }
              if (response.RESULT == "00") {
                setPaymentHandled(true);
                setErrorMessage("");
                localMethod(response, navigate);
                close();
              } else {
                setPaymentHandled(true);
                console.error("Payment failed", response);
                setModal(response);
                close();
              }
            },
            paymentjson,
            (error) => {
              console.error("Payment initialization error:", error);
            }
          );
          $("body").addClass("loaded");
        });
        clearInterval(interval);
      }
    }
    interval = setInterval(tryInit, 100);
    return () => {
      clearInterval(interval);
      $("body").removeClass("loaded");
    };
  }, [paymentjson, HppURL, paymentHandled]);

  // Reset paymentHandled when retrying
  const handleRetry = () => {
    setPaymentHandled(false);
    STATE.setCurrentStep(BuyStep.TB_E_PAYMENT);
    setIsGlobalPaymentsErrorModal(false);
  };

  // Determine previous step based on payment option
  const getPreviousStep = () => {
    const paymentType = selectedPaymentOption?.totalAmountToPayByCustomer
      ? "annual"
      : "monthly";
    return paymentType === "annual"
      ? BuyStep.TB_E_PAYMENT
      : BuyStep.TB_DIRECT_DEBIT_DETAILS;
  };

  if (STATE.paymentProcessLoading) {
    return <Loader text="Processing your payment..." />;
  }
  if (!paymentjson || Object.keys(paymentjson).length === 0 || !HppURL) {
    return <Loader text="Loading payment details..." />;
  }
  return (
    <>
      {isGlobalPaymentsErrorModal && (
        <ModalWrapper
          heading="Payment Failed"
          buttonText="Try again"
          onButtonClick={handleRetry}
          showClose={false}
          simpleText={`Reason: ${errorMessage}`}
          onClose={() => setIsGlobalPaymentsErrorModal(false)}
        />
      )}
      <form>
        <Helmet>
          <meta name="description" content="Redirecting to payment page" />
          <link rel="stylesheet" href={cardCss} />
          <link rel="stylesheet" href={paymentsCss} />
        </Helmet>

        <QuoteFormWrapper>
          <div>
            <h1 className="margin-bottom-10">Payment Details</h1>
            <p className="margin-bottom-30">
              Enter your payment details below.
            </p>
          </div>
          <iframe
            id="iframeId"
            frameBorder="0"
            width="100%"
            height="710px"
            style={{ display: "none", overflowY: "hidden" }}
          />
        </QuoteFormWrapper>
        <FormNavigationButtons
          prevStep={getPreviousStep()}
          nextStep={false}
          isValid={true}
        />
      </form>
    </>
  );
};

export default TbGlobalPaymentsPage;
